import React, { useState, useEffect, useRef } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { lambdaService, FileInfo } from '../../services/lambdaService';
import { TopNav } from '../ui/top-nav';
import { useIntrospectState } from '../../hooks/useIntrospectState';
import {
  InsightsData,
} from '../../types/introspect';
import {
  RefreshCw,
  Sparkles,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Database,
  Columns,
  Hash,
  Send,
  Lightbulb,
  Target,
  Search,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Bot,
  Loader2,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import { ChatMessage } from '../../types/introspect';

export const IntrospectPage: React.FC = () => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [insights, setInsights] = useState<InsightsData | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const {
    selectedFileKey,
    selectFile,
    cacheInsights,
    getCachedInsights,
    clearInsightsCache,
    toggleSection,
    isSectionCollapsed,
    getChatHistory,
    saveChatHistory,
    clearChatHistory,
  } = useIntrospectState();

  const navigate = useNavigate();

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    loadUserInfo();
    loadFiles();
  }, []);

  useEffect(() => {
    if (selectedFileKey) {
      const cached = getCachedInsights(selectedFileKey);
      if (cached) {
        setInsights(cached);
      } else {
        loadInsights(selectedFileKey);
      }
      // Load persisted chat history for this file
      const chatHistory = getChatHistory(selectedFileKey);
      if (chatHistory) {
        setChatMessages(chatHistory.messages);
        setSuggestedQuestions(chatHistory.suggestedQuestions);
      } else {
        // Default suggestions for new file
        setChatMessages([]);
        setSuggestedQuestions([
          "What are the key patterns in this data?",
          "Which columns have the most missing values?",
          "Summarize what this dataset is about",
          "What relationships exist between columns?",
        ]);
      }
    } else {
      setInsights(null);
      setChatMessages([]);
      setSuggestedQuestions([]);
    }
  }, [selectedFileKey]);

  const loadUserInfo = async () => {
    try {
      const user = await getCurrentUser();
      setUserEmail(user.signInDetails?.loginId || user.username || '');
    } catch {
      // Ignore
    }
  };

  const loadFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const result = await lambdaService.listFiles();
      if (result.success && result.files) {
        // Only show parquet files
        const parquetFiles = result.files.filter(
          (f) => f.fileName.endsWith('.parquet') || f.isMultipart
        );
        setFiles(parquetFiles);
      }
    } catch {
      toast.error('Failed to load files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const loadInsights = async (fileKey: string, forceRefresh = false) => {
    setIsLoadingInsights(true);
    try {
      const result = await lambdaService.getFileInsights(fileKey, forceRefresh);
      if (result.success && result.insights) {
        setInsights(result.insights);
        cacheInsights(fileKey, result.insights);
      } else {
        toast.error(result.error || 'Failed to load insights');
        setInsights(null);
      }
    } catch {
      toast.error('Failed to load insights');
      setInsights(null);
    } finally {
      setIsLoadingInsights(false);
    }
  };

  const handleRefreshInsights = () => {
    if (selectedFileKey) {
      clearInsightsCache(selectedFileKey);
      loadInsights(selectedFileKey, true);
    }
  };

  const handleSendMessage = async (message?: string) => {
    const messageToSend = message || chatInput.trim();
    if (!selectedFileKey || !messageToSend) return;

    // Add user message
    const userMessage: ChatMessage = { role: 'user', content: messageToSend };
    const newMessages = [...chatMessages, userMessage];
    setChatMessages(newMessages);
    setChatInput('');
    setIsSendingMessage(true);

    try {
      const result = await lambdaService.chatWithData(
        selectedFileKey,
        messageToSend,
        chatMessages
      );

      if (result.success && result.response) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: result.response,
        };
        const updatedMessages = [...newMessages, assistantMessage];
        setChatMessages(updatedMessages);

        // Update suggested questions
        const newSuggestions = result.suggested_questions && result.suggested_questions.length > 0
          ? result.suggested_questions
          : suggestedQuestions;
        setSuggestedQuestions(newSuggestions);

        // Persist chat history
        saveChatHistory(selectedFileKey, updatedMessages, newSuggestions);
      } else {
        toast.error(result.error || 'Failed to get response');
        // Remove the user message if we couldn't get a response
        setChatMessages((prev) => prev.slice(0, -1));
      }
    } catch {
      toast.error('Failed to chat with data');
      setChatMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleClearChat = () => {
    if (selectedFileKey) {
      setChatMessages([]);
      setSuggestedQuestions([
        "What are the key patterns in this data?",
        "Which columns have the most missing values?",
        "Summarize what this dataset is about",
        "What relationships exist between columns?",
      ]);
      clearChatHistory(selectedFileKey);
      toast.success('Chat history cleared');
    }
  };

  // Helper to clean file names for display - hide internal implementation details
  const getDisplayName = (fileName: string): string => {
    let name = fileName;
    // Remove common extensions
    name = name.replace(/\.(parquet|csv|json|xlsx|xls|tsv)$/i, '');
    // Remove double extensions like .csv.parquet
    name = name.replace(/\.(csv|json)\.(parquet)$/i, '');
    // Remove timestamp prefix (e.g., "1765940893209-")
    if (/^\d+-/.test(name)) {
      name = name.replace(/^\d+-/, '');
    }
    // Remove date-time suffix (e.g., "_20231215_143022")
    name = name.replace(/_\d{8}_\d{6}$/, '');
    // Remove "(X files)" or "(X parts)" suffixes
    name = name.replace(/\s*\(\d+\s+(files?|parts?)\)$/i, '');
    // Remove "_part_X" or "_chunk_X" suffixes
    name = name.replace(/[_-](part|chunk)[_-]?\d+$/i, '');
    // Remove trailing underscores or hyphens
    name = name.replace(/[_-]+$/, '');
    return name;
  };

  const getQualityColor = (score: number): string => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getQualityBadge = (score: number) => {
    if (score >= 80) return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">Excellent</Badge>;
    if (score >= 60) return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">Good</Badge>;
    return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">Needs Attention</Badge>;
  };

  const filteredFiles = files.filter((f) =>
    getDisplayName(f.fileName).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Skeleton loading components
  const StatCardSkeleton = () => (
    <Card className="animate-pulse">
      <CardHeader className="pb-2">
        <div className="h-4 bg-muted rounded w-20" />
      </CardHeader>
      <CardContent>
        <div className="h-8 bg-muted rounded w-16 mb-2" />
        <div className="h-3 bg-muted rounded w-24" />
      </CardContent>
    </Card>
  );

  const TableSkeleton = () => (
    <div className="animate-pulse space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-10 bg-muted rounded" />
      ))}
    </div>
  );

  // Markdown components for custom styling
  const markdownComponents = {
    h1: ({ children }: { children?: React.ReactNode }) => <h1 className="text-xl font-bold mb-2 mt-3">{children}</h1>,
    h2: ({ children }: { children?: React.ReactNode }) => <h2 className="text-lg font-bold mb-2 mt-3">{children}</h2>,
    h3: ({ children }: { children?: React.ReactNode }) => <h3 className="text-base font-semibold mb-1 mt-2">{children}</h3>,
    p: ({ children }: { children?: React.ReactNode }) => <p className="mb-2">{children}</p>,
    ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc ml-4 mb-2 space-y-1">{children}</ul>,
    ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal ml-4 mb-2 space-y-1">{children}</ol>,
    li: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
    code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
      const isBlock = className?.includes('language-');
      if (isBlock) {
        return (
          <pre className="bg-muted/50 rounded-md p-3 my-2 overflow-x-auto font-mono text-xs">
            <code>{children}</code>
          </pre>
        );
      }
      return <code className="bg-muted/50 px-1 py-0.5 rounded text-xs font-mono">{children}</code>;
    },
    pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="border-l-4 border-primary/50 pl-3 my-2 italic text-muted-foreground">{children}</blockquote>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="overflow-x-auto my-2">
        <table className="min-w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th className="border border-border px-2 py-1 bg-muted/50 text-left font-medium">{children}</th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td className="border border-border px-2 py-1">{children}</td>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a href={href} className="text-primary underline" target="_blank" rel="noopener noreferrer">{children}</a>
    ),
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      <TopNav userEmail={userEmail} />
      <div className="flex-1 flex flex-col" style={{ height: 'calc(100vh - 4.75rem)' }}>
        <header className="bg-card border-b border-border z-10 shrink-0">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-base font-semibold text-foreground">Data Insights</h2>
                  <p className="text-sm text-muted-foreground">AI-powered analysis of your data</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-3 py-1.5 rounded-full border border-green-200 dark:border-green-800">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>Powered by AWS Bedrock • Your data stays within AWS</span>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            {/* File Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Select Data File
                </CardTitle>
                <CardDescription>
                  Choose a file to analyze with AI-powered insights
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search files..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select
                    value={selectedFileKey || ''}
                    onValueChange={(value) => selectFile(value || null)}
                  >
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Select a file..." />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingFiles ? (
                        <SelectItem value="loading" disabled>
                          Loading files...
                        </SelectItem>
                      ) : filteredFiles.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No files found
                        </SelectItem>
                      ) : (
                        filteredFiles.map((file) => (
                          <SelectItem key={file.s3Key || file.fileName} value={file.s3Key || file.fileName}>
                            {getDisplayName(file.fileName)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {selectedFileKey && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleRefreshInsights}
                      disabled={isLoadingInsights}
                    >
                      <RefreshCw className={`h-4 w-4 ${isLoadingInsights ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Insights Section - Only show if file selected */}
            {selectedFileKey && (
              <>
                {isLoadingInsights ? (
                  <>
                    {/* Loading Skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCardSkeleton />
                      <StatCardSkeleton />
                      <StatCardSkeleton />
                      <StatCardSkeleton />
                    </div>
                    <Card>
                      <CardHeader>
                        <div className="h-5 bg-muted rounded w-32 animate-pulse" />
                      </CardHeader>
                      <CardContent>
                        <TableSkeleton />
                      </CardContent>
                    </Card>
                  </>
                ) : insights ? (
                  <>
                    {/* Data Quality Overview */}
                    <div
                      className="cursor-pointer"
                      onClick={() => toggleSection('quality')}
                    >
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            <CardTitle>Data Quality</CardTitle>
                            {getQualityBadge(insights.data_quality.overall_score)}
                          </div>
                          {isSectionCollapsed('quality') ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          )}
                        </CardHeader>
                        {!isSectionCollapsed('quality') && (
                          <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <div>
                                <div className="flex justify-between mb-2">
                                  <span className="text-sm text-muted-foreground">Overall Score</span>
                                  <span className={`text-sm font-semibold ${getQualityColor(insights.data_quality.overall_score)}`}>
                                    {insights.data_quality.overall_score}%
                                  </span>
                                </div>
                                <Progress value={insights.data_quality.overall_score} className="h-2" />
                              </div>
                              <div>
                                <div className="flex justify-between mb-2">
                                  <span className="text-sm text-muted-foreground">Null Values</span>
                                  <span className="text-sm font-semibold">
                                    {insights.data_quality.null_percentage.toFixed(1)}%
                                  </span>
                                </div>
                                <Progress
                                  value={100 - insights.data_quality.null_percentage}
                                  className="h-2"
                                />
                              </div>
                              <div>
                                <div className="flex justify-between mb-2">
                                  <span className="text-sm text-muted-foreground">Duplicates</span>
                                  <span className="text-sm font-semibold">
                                    {insights.data_quality.duplicate_percentage.toFixed(1)}%
                                  </span>
                                </div>
                                <Progress
                                  value={100 - insights.data_quality.duplicate_percentage}
                                  className="h-2"
                                />
                              </div>
                            </div>
                            {insights.data_quality.issues.length > 0 && (
                              <div className="mt-4">
                                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                                  Issues Detected
                                </h4>
                                <ul className="space-y-1">
                                  {insights.data_quality.issues.map((issue, i) => (
                                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                      <span className="text-yellow-500">-</span>
                                      {issue}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    </div>

                    {/* Talk to Your Data - Chat Interface */}
                    <Card className="border-primary/20">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="flex items-center gap-2">
                              <MessageSquare className="h-5 w-5 text-primary" />
                              Talk to Your Data
                            </CardTitle>
                            <CardDescription className="mt-1">
                              Ask questions about your data in natural language
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            {chatMessages.length > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleClearChat}
                                className="text-muted-foreground hover:text-destructive hover:border-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Clear Chat
                              </Button>
                            )}
                            <Link to="/ai-connections">
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-orange-500 hover:bg-orange-600 text-white border-orange-500 hover:border-orange-600"
                              >
                                Better with MCP in Claude.ai
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Chat Messages */}
                        <div className="bg-muted/30 rounded-lg p-4 h-[400px] overflow-y-auto space-y-4">
                          {chatMessages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                              <Bot className="h-12 w-12 mb-4 opacity-50" />
                              <p className="text-lg font-medium mb-2">Start a conversation</p>
                              <p className="text-sm max-w-md">
                                Ask me anything about your data - patterns, insights, statistics, or recommendations.
                              </p>
                            </div>
                          ) : (
                            chatMessages.map((msg, i) => (
                              <div
                                key={i}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-[80%] rounded-lg px-4 py-3 ${
                                    msg.role === 'user'
                                      ? 'bg-primary text-primary-foreground'
                                      : 'bg-card border'
                                  }`}
                                >
                                  {msg.role === 'assistant' && (
                                    <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                                      <Bot className="h-3 w-3" />
                                      AI Assistant
                                    </div>
                                  )}
                                  <div className={`text-sm ${msg.role === 'user' ? '' : 'max-w-none'}`}>
                                    {msg.role === 'user' ? (
                                      msg.content
                                    ) : (
                                      <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={markdownComponents}
                                      >
                                        {msg.content}
                                      </ReactMarkdown>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                          {isSendingMessage && (
                            <div className="flex justify-start">
                              <div className="bg-card border rounded-lg px-4 py-3">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span className="text-sm">Analyzing...</span>
                                </div>
                              </div>
                            </div>
                          )}
                          <div ref={chatEndRef} />
                        </div>

                        {/* Suggested Questions */}
                        {suggestedQuestions.length > 0 && chatMessages.length < 6 && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground font-medium">Suggested questions:</p>
                            <div className="flex flex-wrap gap-2">
                              {suggestedQuestions.slice(0, 4).map((question, i) => (
                                <Button
                                  key={i}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-auto py-1.5 px-3"
                                  onClick={() => handleSendMessage(question)}
                                  disabled={isSendingMessage}
                                >
                                  {question}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Chat Input */}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Ask a question about your data..."
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                              }
                            }}
                            disabled={isSendingMessage}
                            className="flex-1"
                          />
                          <Button
                            onClick={() => handleSendMessage()}
                            disabled={isSendingMessage || !chatInput.trim()}
                          >
                            {isSendingMessage ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Business Insights */}
                    <div
                      className="cursor-pointer"
                      onClick={() => toggleSection('business')}
                    >
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Lightbulb className="h-5 w-5 text-primary" />
                            <CardTitle>Business Insights</CardTitle>
                          </div>
                          {isSectionCollapsed('business') ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          )}
                        </CardHeader>
                        {!isSectionCollapsed('business') && (
                          <CardContent className="space-y-6">
                            <div>
                              <h4 className="text-sm font-medium mb-2">Summary</h4>
                              <p className="text-muted-foreground">{insights.business_insights.summary}</p>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                Recommendations
                              </h4>
                              <ul className="space-y-2">
                                {insights.business_insights.recommendations.map((rec, i) => (
                                  <li key={i} className="flex items-start gap-2 text-muted-foreground">
                                    <span className="text-primary font-bold">{i + 1}.</span>
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <Target className="h-4 w-4 text-blue-500" />
                                Potential Use Cases
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {insights.business_insights.potential_use_cases.map((useCase, i) => (
                                  <Badge key={i} variant="secondary">
                                    {useCase}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    </div>

                    {/* Statistical Summary */}
                    <div
                      className="cursor-pointer"
                      onClick={() => toggleSection('stats')}
                    >
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Hash className="h-5 w-5 text-primary" />
                            <CardTitle>Statistical Summary</CardTitle>
                          </div>
                          {isSectionCollapsed('stats') ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          )}
                        </CardHeader>
                        {!isSectionCollapsed('stats') && (
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                              <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                  <Database className="h-4 w-4" />
                                  <span className="text-xs">Rows</span>
                                </div>
                                <p className="text-2xl font-bold">
                                  {insights.statistical_summary.row_count.toLocaleString()}
                                </p>
                              </div>
                              <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                  <Columns className="h-4 w-4" />
                                  <span className="text-xs">Columns</span>
                                </div>
                                <p className="text-2xl font-bold">
                                  {insights.statistical_summary.column_count}
                                </p>
                              </div>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                              <div className="max-h-[320px] overflow-auto">
                                <Table>
                                  <TableHeader className="sticky top-0 bg-background z-10">
                                    <TableRow>
                                      <TableHead>Column</TableHead>
                                      <TableHead>Type</TableHead>
                                      <TableHead className="text-right">Nulls</TableHead>
                                      <TableHead className="text-right">Unique</TableHead>
                                      <TableHead className="text-right">Min</TableHead>
                                      <TableHead className="text-right">Max</TableHead>
                                      <TableHead className="text-right">Mean</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {insights.statistical_summary.columns.map((col, i) => (
                                      <TableRow key={i}>
                                        <TableCell className="font-medium">{col.name}</TableCell>
                                        <TableCell>
                                          <Badge variant="outline">{col.type}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">{col.null_count.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">{col.unique_count.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">
                                          {col.min !== null && col.min !== undefined ? String(col.min) : '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {col.max !== null && col.max !== undefined ? String(col.max) : '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {col.mean !== null && col.mean !== undefined
                                            ? typeof col.mean === 'number'
                                              ? col.mean.toFixed(2)
                                              : String(col.mean)
                                            : '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    </div>
                  </>
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground text-center">
                        Unable to load insights for this file.
                        <br />
                        <span className="text-sm">Try refreshing or selecting a different file.</span>
                      </p>
                      <Button variant="outline" className="mt-4" onClick={handleRefreshInsights}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Retry
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Empty state when no file selected */}
            {!selectedFileKey && !isLoadingFiles && (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Sparkles className="h-16 w-16 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Select a File to Get Started</h3>
                  <p className="text-muted-foreground text-center max-w-md">
                    Choose a data file from the dropdown above to view AI-generated insights,
                    statistical summaries, and chat with your data.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
